import { isClosedDateStatement } from './dateStatement';
/**
 * Summarizes subfield $a for PERSONAL type: "FamilyName, GivenName"
 * Returns empty string if no family name provided.
 */
export var summarizeSubfieldA = function (familyName, givenName) {
    var family = familyName.trim();
    var given = givenName.trim();
    // PERSONAL ind1=0 (forename-only heading): familyName is empty by design;
    // return the given name alone so the editor and proposal-save path still
    // produce a non-empty $a value. See TASK-163 (Šťastná regression report
    // 19.05.2026 — example: Ignác of Antioch).
    if (!family && !given)
        return '';
    if (!family)
        return given;
    if (!given)
        return family;
    return "".concat(family, ", ").concat(given);
};
/**
 * Parses a flat PERSONAL `$a` string back into FAMILY_NAME + GIVEN_NAME
 * components (CZNA-310). Inverse of `summarizeSubfieldA`.
 *
 * This is the load-side counterpart needed for imported (Aleph) records whose
 * `$a` is a flat string with no persisted component metadata. Without it, the
 * surname-led (`ind1='1'`) and forename-only (`ind1='0'`) heading inputs stay
 * empty on edit and a no-op save would drop the `100` heading entirely.
 *
 * The MARC `ind1` discriminator disambiguates the format (mirrors
 * `summarizeSubfieldA` / `generateHeadingValue`):
 *   - `ind1='1'` (surname-led): `"Family, Given"` → split on the FIRST comma;
 *     when no comma is present the whole value is the family name.
 *   - `ind1='0'` (forename-only): `summarizeSubfieldA('', given)` emits the
 *     given name alone, so the flat value is GIVEN_NAME and FAMILY_NAME is ''.
 *   - default: treated as surname-led for backward compatibility.
 *
 * Round-trip property: for any (family, given) pair where `summarizeSubfieldA`
 * is the encoder, `parseSubfieldA(summarizeSubfieldA(f, g), ind1)` recovers the
 * same components (modulo whitespace trimming the encoder already applies).
 */
export var parseSubfieldA = function (rawA, ind1) {
    var raw = (rawA || '').trim();
    if (!raw)
        return { FAMILY_NAME: '', GIVEN_NAME: '' };
    // Forename-only heading — the entire $a is the given name (no surname).
    if (ind1 === '0')
        return { FAMILY_NAME: '', GIVEN_NAME: raw };
    // Surname-led (ind1='1' or default): "Family, Given". Split on the FIRST
    // comma so given names that themselves contain commas stay intact.
    var commaIdx = raw.indexOf(',');
    if (commaIdx === -1)
        return { FAMILY_NAME: raw, GIVEN_NAME: '' };
    var family = raw.slice(0, commaIdx).trim();
    var given = raw.slice(commaIdx + 1).trim();
    return { FAMILY_NAME: family, GIVEN_NAME: given };
};
/**
 * A floruit statement — `činný 16. století`, `činná 1670` — says when a person
 * was ACTIVE, for someone whose birth and death years are unknown. It is not a
 * birth date, so it does not open a range: appending the "still living" hyphen
 * turns "active in the 16th century" into "active from the 16th century
 * onwards", which is a different and false claim.
 *
 * <p>The rule is measured, not assumed (CZNA-783). Of the 28 605 floruit values
 * in the live corpus, 28 600 carry NO trailing hyphen and 5 do — while 1 136
 * long dates (`1920 duben 15.-`) legitimately do, which is why this predicate
 * names the floruit marker rather than "anything that is not a bare year".
 *
 * <p><b>Built with `new RegExp`, not written as a literal, and that is
 * load-bearing.</b> `stripes-webpack` hands every `.ts` to ts-loader under its
 * own tsconfig, which sets no `target` — so tsc's ES5 default applies and a
 * regex LITERAL carrying `u` is a fatal TS1501. The module's own
 * `"target": "ES2020"` is used only by `tsc --noEmit`, so both local gates stay
 * green and the platform build is what breaks. Same reason, same shape as
 * `src/utils/searchQueryGuard.ts`.
 */
var FLORUIT = new RegExp('^\\s*činn[ýáéí](?![\\p{L}])', 'iu');
var isFloruit = function (value) { return FLORUIT.test(value); };
/**
 * Whether a lone first half should be reopened with the "still living / still
 * ongoing" hyphen. Three shapes say no, and each is measured rather than assumed:
 *
 * <ul>
 * <li>a floruit (`činný 1670`) — see {@link isFloruit};</li>
 * <li>a statement CLOSED by ISBD punctuation (`2003 :`, `1464)`) — the corporate
 *     `$d` case of CZNA-876. `$d 2003 : $c Alicante` says the meeting was held in
 *     2003; appending a hyphen turns that into "held from 2003 onwards". Of the
 *     5 987 corporate `$d` values 4 913 (82,1 %) were rewritten this way the
 *     moment a cataloguer edited the record, `2003 :` → `2003 :-` being 3 351 of
 *     them;</li>
 * <li>a first half that ALREADY carries a hyphen, i.e. is itself a range or is
 *     itself already open. Reopening it emits `1920-1935-` or `1960--`, which is
 *     not a date in any reading.</li>
 * </ul>
 *
 * <p><b>That third clause is not defensive coding.</b> `parseSubfieldD` splits on
 * the first hyphen, so a first half decoded from a flat value never holds one —
 * but 1 025 corporate records (17,1 % of the field) do not go through the decoder
 * at all. They carry a `record_data_component` row whose `FOUNDATION_DATE` is the
 * WHOLE flat value (`FOUNDATION_DATE = 1920-1935`, no `DISSOLUTION_DATE`), which
 * the load path trusts; because components are present, CZNA-310's
 * `importedValue` passthrough is never armed. A plain open-and-save therefore
 * rewrote every one of them — `1920-1935` → `1920-1935-`, `1960-` → `1960--` —
 * with no edit of any kind. Measured 31. 8. 2026: all 1 025, in exactly the five
 * shapes `9999-9999`, `9999-`, `9999`, `9999 -`, `9999?-9999`.
 *
 * <p>Everything else keeps the hyphen, which is the personal-name default: a
 * cataloguer who types 1950 into "Rok narození" and leaves "Rok úmrtí" empty
 * means a living person, and `1950-` is what MARC wants.
 */
var shouldOpenRange = function (first) {
    return !isFloruit(first) && !isClosedDateStatement(first) && first.indexOf('-') === -1;
};
/**
 * Summarizes subfield $d for dates: `"birth-death"`, `"birth-"` (still living /
 * still active) or `"-death"` (birth unknown). Empty only when BOTH parts are.
 *
 * <p><b>The `"-death"` branch is not symmetry for its own sake (CZNA-783).</b>
 * `parseSubfieldD` splits on the first hyphen, so a catalogued `"-1605"` — a
 * person whose birth year is unknown — decomposes to `birth=''`,
 * `death='1605'`. This function used to answer `''` to that pair, and
 * `genericFormToProposalData` drops a subfield whose summary is empty
 * (`if (!summarized.trim()) return;`). The `$d` therefore VANISHED the moment a
 * cataloger retyped the death box: CZNA-310's `importedValue` passthrough
 * shields an untouched field, and editing is exactly what steps outside it.
 * Measured over the live corpus, 18 818 of 1 265 926 `$d` occurrences (1.49 %)
 * are in that shape — `-1605`, `-asi 1600`, `-645 př. Kr.`
 */
export var summarizeSubfieldD = function (birthDate, deathDate) {
    var birth = birthDate.trim();
    var death = (deathDate === null || deathDate === void 0 ? void 0 : deathDate.trim()) || '';
    if (!birth && !death)
        return '';
    if (!birth)
        return "-".concat(death);
    if (!death)
        return shouldOpenRange(birth) ? "".concat(birth, "-") : birth;
    return "".concat(birth, "-").concat(death);
};
/**
 * Parses a flat `$d` date string back into BIRTH_DATE + DEATH_DATE components
 * (CZNA-310). Inverse of `summarizeSubfieldD` (and `summarizeCorporateD`,
 * which delegates to it). Used for imported records whose `$d` is a flat
 * string with no component metadata.
 *
 * `summarizeSubfieldD` emits `"birth-death"` or `"birth-"` (open-ended), so we
 * split on the FIRST hyphen: everything before it is the birth/foundation
 * date, everything after is the death/dissolution date (possibly empty).
 * A value with no hyphen is treated as a bare birth date.
 *
 * The same shape feeds both PERSONAL ($d BIRTH/DEATH) and CORPORATE
 * ($d FOUNDATION/DISSOLUTION); the caller remaps the keys via the
 * SUMMARIZE_ARG_MAP component-type pair.
 *
 * <p><b>A floruit that ends in a hyphen keeps it</b> (CZNA-783). `činný 1894-`
 * — active from 1894, still active — would otherwise decode to
 * `('činný 1894', '')`, which is indistinguishable from a plain
 * `činný 1894`; `summarizeSubfieldD` does not reopen a range on a floruit, so
 * re-encoding would drop the hyphen and change the claim. Five corpus
 * occurrences, and precisely the kind of silent rewrite this ticket exists to
 * remove — so the decoder keeps the distinction rather than throwing it away.
 * A floruit RANGE (`činný 16. století-17. století`) has content after the
 * hyphen and splits normally.
 */
export var parseSubfieldD = function (rawD) {
    var raw = (rawD || '').trim();
    if (!raw)
        return { first: '', second: '' };
    if (isFloruit(raw) && raw.endsWith('-'))
        return { first: raw, second: '' };
    var hyphenIdx = raw.indexOf('-');
    if (hyphenIdx === -1)
        return { first: raw, second: '' };
    var first = raw.slice(0, hyphenIdx).trim();
    var second = raw.slice(hyphenIdx + 1).trim();
    return { first: first, second: second };
};
/**
 * Summarizes subfield $d for corporate dates: "foundationDate-dissolutionDate"
 * Same format as personal dates but uses FOUNDATION_DATE and DISSOLUTION_DATE components.
 */
export var summarizeCorporateD = function (foundationDate, dissolutionDate) {
    return summarizeSubfieldD(foundationDate, dissolutionDate);
};
/**
 * Summarizes subfield $a for PERSONAL family-name records (TASK-115, `ind1='3'`).
 * Format: "FamilyName (qualifier)". The qualifier is composed into `$a` as a
 * parenthesised suffix — it is NOT a separate MARC subfield. When qualifier is
 * empty, falls back to the bare name; when name is empty, returns ''.
 *
 * Round-trip pair: see `parseFamilyA` for the inverse transform.
 */
export var summarizeFamilyA = function (familyName, qualifier) {
    var name = (familyName || '').trim();
    var qual = (qualifier || '').trim();
    if (!name)
        return '';
    if (!qual)
        return name;
    return "".concat(name, " (").concat(qual, ")");
};
/**
 * Parses a family-name `$a` text back into structured parts (TASK-115).
 * Inverse of `summarizeFamilyA`. Uses a greedy outer-paren regex so that names
 * which themselves contain parentheses are preserved as the family name, and
 * only the trailing parenthesised suffix becomes the qualifier:
 *
 *   "Habsburkové (rod)"           → { familyName: 'Habsburkové', qualifier: 'rod' }
 *   "Habsburkové (rod),"          → { familyName: 'Habsburkové', qualifier: 'rod' }
 *   "Habsburkové"                 → { familyName: 'Habsburkové', qualifier: null }
 *   "Habsburkové (junior) (rod)"  → { familyName: 'Habsburkové (junior)', qualifier: 'rod' }
 *   ""                            → { familyName: '', qualifier: null }
 *
 * <p><b>Trailing ISBD punctuation is not part of the qualifier</b> (CZNA-1007).
 * The pattern used to be anchored hard at end-of-string, so the 146 corpus
 * `100`/`400` rows that end `(rod),` — the comma that introduces `$d` — never
 * matched at all: the whole string became the family name and the qualifier
 * box came up empty on exactly the records a cataloguer would want to correct.
 * The punctuation is dropped here rather than kept, because
 * `deriveComponentAffix` peels it off the stored value on load and
 * `computeSummarizedValue` puts it back on save — the same mechanism that
 * keeps `Kaska, Hynek,` intact on the surname-led branch. Without the affix
 * (an unedited row) the CZNA-898 snapshot re-emits the stored text verbatim,
 * so nothing depends on this function reproducing the comma.
 *
 * The parsed qualifier is NOT checked against an allow-list. The pick list
 * (`rod`, `rodina`) is the component's `selectOptions` in `personal.json`, and
 * the renderer shows it as a `<datalist>` on a free-text input — a suggestion,
 * not a closed set — so a qualifier the corpus holds but the list does not
 * offer survives being read and typed. 23 of the 2 684 corpus family headings
 * carry neither of the two: 3 hold another word (`famiglia`, `porodica`,
 * `sem'ja`) and 20 hold no qualifier at all.
 *
 * The figure is re-measurable, which is the point of writing the query down —
 * it was recorded once as `23 of 2 661` and 2 661 is the CONFORMING subset,
 * not the total. Note the `[[:space:],.;:]*$`: without it the 146 rows that
 * end `(rod),` fall out of the bucket they belong to, the same trailing
 * punctuation this function exists to handle.
 *
 * ```sql
 * SELECT count(*) AS total,
 *        count(*) FILTER (WHERE q IN ('rod','rodina'))                AS on_list,
 *        count(*) FILTER (WHERE q IS NOT NULL AND q NOT IN ('rod','rodina')) AS off_list,
 *        count(*) FILTER (WHERE q IS NULL)                            AS no_qualifier
 * FROM (SELECT substring(d.value from '\(([^()]*)\)[[:space:],.;:]*$') AS q
 *       FROM record_data d JOIN record r ON r.id = d.record_id
 *       WHERE d.field_tag IN ('100','400') AND d.indicator1 = '3'
 *         AND d.subfield_code = 'a' AND r.status = 'ACTIVE') fam;
 * -- 2026-09-22, local corpus of 1 296 616 records:
 * --   total 2684 | on_list 2661 | off_list 3 | no_qualifier 20
 * ```
 */
export var parseFamilyA = function (rawA) {
    var raw = (rawA || '').trim();
    if (!raw)
        return { familyName: '', qualifier: null };
    // Greedy outer: capture everything up to the LAST `(...)` group on the line,
    // allowing the ISBD punctuation that may follow it.
    var match = raw.match(/^(.+?)\s*\(([^()]+)\)\s*[,.;:]*\s*$/);
    if (!match)
        return { familyName: raw, qualifier: null };
    var familyName = match[1].trim();
    var qualifier = match[2].trim();
    if (!familyName)
        return { familyName: raw, qualifier: null };
    return { familyName: familyName, qualifier: qualifier.length > 0 ? qualifier : null };
};
/*
 * The encoder/decoder registries that used to live here (`SUMMARIZE_FN_MAP`,
 * the positional `SUMMARIZE_ARG_MAP`, and the `switch` inside
 * `parseSummarizedValue`) moved to `../codec/codecRegistry.ts` in CZNA-371 —
 * ADR-0005 D8 places the codec whitelist under `ProposalForm/codec/`. This
 * module keeps only the pure transform functions; `computeSummarizedValue`
 * and `parseSummarizedValue` are exported from the registry.
 */
/*
 * `generateHeadingValue(type, FormState, ind1?)` used to sit here — the pane
 * title of `LegacyForm`, computed from the flat legacy form state. It went with
 * that form in CZNA-487. Its successor, the CZNA-485 `headingPreview` recipe,
 * was still a second implementation of `HeadingComposer`; since CZNA-1418 the
 * pane title is composed by the backend itself (`hooks/useHeadingPreview.ts`).
 */
/**
 * Reads the live `100 ind1` value from a generic form-state snapshot (TASK-115).
 * Returns the indicator value or `'1'` (surname-led) when the field-instance
 * is missing or the indicator slot has not been seeded. Used by RecordForm to
 * branch the pane title heading per heading type.
 */
export var getPersonalInd1 = function (formData) {
    var _a, _b, _c;
    var instance = (_b = (_a = formData === null || formData === void 0 ? void 0 : formData.fields) === null || _a === void 0 ? void 0 : _a['100']) === null || _b === void 0 ? void 0 : _b[0];
    var ind1 = (_c = instance === null || instance === void 0 ? void 0 : instance._ind1) === null || _c === void 0 ? void 0 : _c.value;
    return (typeof ind1 === 'string' && ind1.length > 0) ? ind1 : '1';
};
